/**
 * Vendor buying mode — what an order for a vendor IS, and whether anyone ever
 * said so.
 *
 * `vendors.fulfillment_type` decides the shape of an order: a purchase order
 * emailed to the vendor ('delivery'), or a shopping list somebody takes to the
 * shop ('pickup'). It is NOT NULL DEFAULT 'delivery', so on its own it cannot
 * tell an ANSWER from a DEFAULT — a vendor nobody has ever been asked about
 * looks exactly like one that was asked and said "delivery".
 *
 * `vendors.fulfillment_type_source` is the missing bit:
 *   'default' — nobody has been asked. The question fires once, at Finish.
 *   'user'    — a person chose (vendor editor, or the question itself).
 *   'import'  — inferred from evidence by an import path. Counts as answered.
 *   'history' — inferred from this store's own unambiguous order history by
 *               migration 20260814130000. Counts as answered.
 *
 * ── Why an unanswered vendor gets NO pill ────────────────────────────────────
 *
 * Unanswered is not a fault. The order still works, the question is still
 * asked once at Finish, and nothing about the vendor is broken. A pill in the
 * row that also carries "Not linked" (a real defect) and "Finished" reads as a
 * problem report, and in production 2,514 of 2,526 vendors are unanswered — a
 * badge that appears on nearly every column tells the user nothing except that
 * something is wrong everywhere.
 *
 * So `buyingModePill` returns **null** when nobody has answered. The place to
 * ask is the moment the answer matters (the Finish button, which already reads
 * a neutral "order"), not a permanent alarm on the vendor's name.
 *
 * Pure: no React, no network, no Supabase client. `needsBuyingModeAnswer` lives
 * here, and services/vendorFulfillmentService re-exports it, so there is
 * exactly ONE definition of "unanswered" in the app — a second, subtly
 * different one is how a vendor gets asked twice.
 */

export const DELIVERY = 'delivery';
export const PICKUP = 'pickup';
export const UNKNOWN = 'unknown';

/** The provenance value that means "nobody has been asked". */
export const UNANSWERED_SOURCE = 'default';

/**
 * Every provenance that COUNTS AS ANSWERED. Adding one here is the only way a
 * new source stops the question firing — no caller may re-list these inline.
 */
export const ANSWERED_SOURCES = Object.freeze(['user', 'import', 'history']);

/** The two answers the question offers, verbatim. */
export const DELIVERY_ANSWER_LABEL = 'They deliver — send a purchase order';
export const PICKUP_ANSWER_LABEL = 'We go and buy it — make a shopping list';

export const BUYING_MODE_ANSWERS = Object.freeze([
  Object.freeze({
    mode: DELIVERY,
    label: DELIVERY_ANSWER_LABEL,
    sub: 'We email the order to your vendor contact.',
  }),
  Object.freeze({
    mode: PICKUP,
    label: PICKUP_ANSWER_LABEL,
    sub: 'We build a list for your phone, or to print and take with you.',
  }),
]);

/**
 * How the current value got there, in plain words. Shown wherever a user can
 * change the mode, so "why does it say that?" never needs a support ticket.
 */
export const SOURCE_DESCRIPTIONS = Object.freeze({
  user: 'You set this',
  import: 'From your imported vendor details',
  history: 'From your order history',
  default: 'Not set yet',
});

const PILL_TONES = Object.freeze(['neutral', 'info']);

/** Read a field from either a raw DB row or an already-camelCased view model. */
function readField(vendor, snake, camel) {
  if (!vendor) return undefined;
  const snakeValue = vendor[snake];
  if (snakeValue !== undefined && snakeValue !== null) return snakeValue;
  const camelValue = vendor[camel];
  if (camelValue !== undefined && camelValue !== null) return camelValue;
  return undefined;
}

/** What the column literally holds today — default included. */
function readStoredType(vendor) {
  if (vendor && (vendor.isPickup === true || vendor.is_pickup === true)) return PICKUP;
  const raw = readField(vendor, 'fulfillment_type', 'fulfillmentType');
  return String(raw ?? '').toLowerCase() === PICKUP ? PICKUP : DELIVERY;
}

/** Anything unrecognized reads as unanswered — the safe direction, because the
 *  only cost of "unanswered" is being asked once. */
function readSource(vendor) {
  const raw = readField(vendor, 'fulfillment_type_source', 'fulfillmentTypeSource');
  const value = String(raw ?? '').toLowerCase();
  return ANSWERED_SOURCES.includes(value) ? value : UNANSWERED_SOURCE;
}

/**
 * Can this vendor's answer be written at all?
 *
 * A legacy vendor is a row synthesised from inventory data with a slug for an
 * id — there is nothing to write to, so it is never asked and never pilled.
 */
export function isAnswerableVendor(vendor) {
  if (!vendor || vendor.isLegacy) return false;
  return vendor.id != null || vendor.vendorId != null;
}

/**
 * The one reading of a vendor's buying mode.
 *
 * @returns {{mode: 'delivery'|'pickup'|'unknown', answered: boolean,
 *            source: 'default'|'user'|'import'|'history', storedType: 'delivery'|'pickup'}}
 *
 * `mode` is 'unknown' until somebody (or unambiguous history) answered —
 * it is deliberately NOT the column's default. `storedType` is what the column
 * holds and therefore what a fan-out would do right now; read it only when you
 * genuinely mean "what will happen", never to display an answer nobody gave.
 */
export function resolveBuyingMode(vendor) {
  const source = readSource(vendor);
  const answered = ANSWERED_SOURCES.includes(source);
  const storedType = readStoredType(vendor);
  return { mode: answered ? storedType : UNKNOWN, answered, source, storedType };
}

/**
 * True when this vendor still needs the question.
 *
 * Not simply `!answered`: an unanswerable vendor (legacy, or no id) is not
 * pending, it is out of scope.
 */
export function needsBuyingModeAnswer(vendor) {
  if (!isAnswerableVendor(vendor)) return false;
  return !resolveBuyingMode(vendor).answered;
}

/**
 * The badge for a vendor's buying mode, or null when there is nothing worth
 * saying.
 *
 * @returns {null | {text: 'Delivery'|'Pickup', tone: 'neutral'|'info', title: string}}
 *
 * Contract for callers:
 *   - null  → render NOTHING. Not an empty pill, not a placeholder, and above
 *             all not a warning. Unanswered is not a fault; the question is
 *             asked once at Finish and the button already reads a neutral
 *             "order".
 *   - tone 'neutral' → the plain tag style (Delivery: the ordinary case).
 *   - tone 'info'    → the quiet blue-grey tag style (Pickup: worth noticing,
 *                      because this order becomes a list, not an email).
 *   There is deliberately no warning/alert tone. If a caller ever needs one
 *   for this fact, the fact is not the problem.
 */
export function buyingModePill(vendor) {
  const { mode, answered } = resolveBuyingMode(vendor);
  if (!answered || mode === UNKNOWN) return null;
  if (mode === PICKUP) {
    return {
      text: 'Pickup',
      tone: 'info',
      title: 'You buy this in person — orders become shopping lists',
    };
  }
  return {
    text: 'Delivery',
    tone: 'neutral',
    title: 'They deliver — orders become purchase orders emailed to your contact',
  };
}

/** Plain-words provenance for a settings screen: "You set this", etc. */
export function buyingModeSourceText(vendor) {
  const { source } = resolveBuyingMode(vendor);
  return SOURCE_DESCRIPTIONS[source] || SOURCE_DESCRIPTIONS.default;
}

/** One sentence saying what an order for this vendor will become. */
export function buyingModeExplanation(vendor) {
  const { mode, storedType } = resolveBuyingMode(vendor);
  if (mode === UNKNOWN) {
    // Deliberately does not repeat SOURCE_DESCRIPTIONS.default — the two are
    // rendered side by side ("Not set yet · We will ask you…").
    return 'We will ask you the first time you order from this vendor.';
  }
  return storedType === PICKUP
    ? 'Orders become shopping lists — on your phone, or printed to take with you.'
    : 'Orders become purchase orders, emailed to your contact.';
}

export const BUYING_MODE_PILL_TONES = PILL_TONES;
